export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

// String-based log level type for settings
export type LogLevelString = 'debug' | 'info' | 'warn' | 'error' | 'none';

// Mapping between string and enum
const LOG_LEVEL_MAP: Record<LogLevelString, LogLevel> = {
  debug: LogLevel.DEBUG,
  info: LogLevel.INFO,
  warn: LogLevel.WARN,
  error: LogLevel.ERROR,
  none: LogLevel.NONE,
};

export interface LogContext {
  [key: string]: any;
}

export interface LogMetadata {
  browser: string;
  viewport: string;
  url: string;
  userAgent: string;
}

export interface LogEntry {
  timestamp: string;
  level: string;
  component: string;
  message: string;
  context?: LogContext;
  metadata?: LogMetadata;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

class Logger {
  private static instance: Logger;
  private logBuffer: LogEntry[] = [];
  private maxBufferSize: number = 1000;

  private constructor() {}

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private getMetadata(): LogMetadata {
    return {
      browser: this.getBrowserInfo(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.pathname,
      userAgent: navigator.userAgent,
    };
  }

  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    let version = 'Unknown';

    if (ua.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
      version = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || version;
    } else if (ua.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
      version = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || version;
    } else if (ua.indexOf('Safari') > -1) {
      browserName = 'Safari';
      version = ua.match(/Version\/(\d+\.\d+)/)?.[1] || version;
    } else if (ua.indexOf('Edge') > -1) {
      browserName = 'Edge';
      version = ua.match(/Edge\/(\d+\.\d+)/)?.[1] || version;
    }

    return `${browserName} ${version}`;
  }

  private getGlobalLogLevel(): LogLevel {
    try {
      // Read from operational settings
      const operationalSettings = localStorage.getItem('operational_view_settings');
      if (operationalSettings) {
        const parsed = JSON.parse(operationalSettings);
        if (parsed.logLevel && LOG_LEVEL_MAP[parsed.logLevel as LogLevelString] !== undefined) {
          return LOG_LEVEL_MAP[parsed.logLevel as LogLevelString];
        }
      }
    } catch (error) {
      console.error('Error reading log level from operational settings:', error);
    }

    // Default to ERROR for cleaner console (super_admin can enable debug via settings)
    return LogLevel.ERROR;
  }

  private shouldLog(level: LogLevel, component: string): boolean {
    const config = (window as any).__PANTHER_LOG_CONFIG__;

    // If no custom config, use operational settings
    if (!config) {
      return level >= this.getGlobalLogLevel();
    }

    if (!config.enabled) return false;

    if (config.disabledComponents?.includes(component)) return false;

    const componentLevel = config.componentLevels?.[component];
    if (componentLevel !== undefined) {
      return level >= componentLevel;
    }

    // Use operational settings as fallback for global level
    return level >= (config.globalLevel !== undefined ? config.globalLevel : this.getGlobalLogLevel());
  }

  private formatLog(
    level: LogLevel,
    component: string,
    message: string,
    context?: LogContext,
    error?: Error
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      component,
      message,
      metadata: this.getMetadata(),
    };

    if (context && Object.keys(context).length > 0) {
      entry.context = context;
    }

    if (error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return entry;
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  private log(
    level: LogLevel,
    component: string,
    message: string,
    context?: LogContext,
    error?: Error
  ): void {
    if (!this.shouldLog(level, component)) return;

    const entry = this.formatLog(level, component, message, context, error);
    this.addToBuffer(entry);

    const config = (window as any).__PANTHER_LOG_CONFIG__;
    const prettyPrint = config?.prettyPrint !== false;

    if (prettyPrint) {
      const levelColors: Record<string, string> = {
        DEBUG: '#6B7280',
        INFO: '#3B82F6',
        WARN: '#F59E0B',
        ERROR: '#EF4444',
      };

      const color = levelColors[entry.level] || '#000000';

      console.groupCollapsed(
        `%c[${entry.level}] ${entry.component} %c${entry.message}`,
        `color: ${color}; font-weight: bold`,
        'color: inherit'
      );

      console.log('Timestamp:', entry.timestamp);
      if (entry.context) {
        console.log('Context:', entry.context);
      }
      if (entry.error) {
        console.error('Error:', entry.error);
      }
      console.log('Metadata:', entry.metadata);
      console.log('Raw JSON:', JSON.stringify(entry, null, 2));
      console.groupEnd();
    } else {
      console.log(JSON.stringify(entry));
    }
  }

  public debug(component: string, message: string, context?: LogContext): void {
    this.log(LogLevel.DEBUG, component, message, context);
  }

  public info(component: string, message: string, context?: LogContext): void {
    this.log(LogLevel.INFO, component, message, context);
  }

  public warn(component: string, message: string, context?: LogContext): void {
    this.log(LogLevel.WARN, component, message, context);
  }

  // Overloaded signatures for error method
  public error(component: string, message: string, error: Error, context?: LogContext): void;
  public error(component: string, message: string, context?: LogContext): void;
  public error(
    component: string,
    message: string,
    errorOrContext?: Error | LogContext,
    context?: LogContext
  ): void {
    // If third parameter is an Error object, use it as error
    if (errorOrContext instanceof Error) {
      this.log(LogLevel.ERROR, component, message, context, errorOrContext);
    } else {
      // Otherwise, treat it as context
      this.log(LogLevel.ERROR, component, message, errorOrContext, undefined);
    }
  }

  public getBuffer(): LogEntry[] {
    return [...this.logBuffer];
  }

  public clearBuffer(): void {
    this.logBuffer = [];
  }

  public exportLogs(): void {
    const logs = this.getBuffer();
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pantheraccess-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Update the global log level (for settings integration)
   * This doesn't persist - settings should be saved via OperationalSettingsContext
   */
  public setLogLevel(level: LogLevelString): void {
    const config = (window as any).__PANTHER_LOG_CONFIG__ || {};
    config.globalLevel = LOG_LEVEL_MAP[level];
    (window as any).__PANTHER_LOG_CONFIG__ = config;
  }

  /**
   * Get current effective log level
   */
  public getCurrentLogLevel(): LogLevel {
    const config = (window as any).__PANTHER_LOG_CONFIG__;
    if (config && config.globalLevel !== undefined) {
      return config.globalLevel;
    }
    return this.getGlobalLogLevel();
  }

  /**
   * Get current log level as string
   */
  public getCurrentLogLevelString(): LogLevelString {
    const level = this.getCurrentLogLevel();
    const entry = Object.entries(LOG_LEVEL_MAP).find(([_, val]) => val === level);
    return (entry?.[0] || 'error') as LogLevelString;
  }
}

export const logger = Logger.getInstance();
export default logger;
